/**
 * Detects visits that arrive from an AI assistant (ChatGPT, Perplexity, Claude,
 * Gemini, Copilot and friends), keeps the value sticky for the whole session and
 * reports it to Google Analytics once per session.
 */

const STORAGE_KEY = 'byteline_ai_referral';

type Matcher = { test: (host: string) => boolean; name: string };

const host = (needle: string): Matcher['test'] => (h: string) => h === needle || h.endsWith(`.${needle}`);
const contains = (needle: string): Matcher['test'] => (h: string) => h.includes(needle);

const HOST_MATCHERS: Matcher[] = [
  { test: contains('chatgpt.com'), name: 'ChatGPT' },
  { test: contains('chat.openai.com'), name: 'ChatGPT' },
  { test: contains('openai.com'), name: 'ChatGPT' },
  { test: contains('perplexity.ai'), name: 'Perplexity' },
  { test: contains('claude.ai'), name: 'Claude' },
  { test: contains('anthropic.com'), name: 'Claude' },
  { test: contains('gemini.google.com'), name: 'Gemini' },
  { test: contains('bard.google.com'), name: 'Gemini' },
  { test: contains('aistudio.google.com'), name: 'Gemini' },
  { test: contains('copilot.microsoft.com'), name: 'Microsoft Copilot' },
  { test: contains('bing.com/chat'), name: 'Microsoft Copilot' },
  { test: contains('edgeservices.bing.com'), name: 'Microsoft Copilot' },
  { test: contains('you.com'), name: 'You.com' },
  { test: contains('phind.com'), name: 'Phind' },
  { test: contains('poe.com'), name: 'Poe' },
  { test: contains('grok.com'), name: 'Grok' },
  { test: contains('x.ai'), name: 'Grok' },
  { test: contains('mistral.ai'), name: 'Le Chat' },
  { test: contains('meta.ai'), name: 'Meta AI' },
  { test: contains('deepseek.com'), name: 'DeepSeek' },
  { test: contains('kimi.moonshot'), name: 'Kimi' },
  { test: contains('clova'), name: 'CLOVA X' },
  { test: contains('wrtn.ai'), name: 'Wrtn' },
  { test: host('t.co'), name: '' }, // never matches, keeps list shape explicit
];

const UTM_MATCHERS: Record<string, string> = {
  chatgpt: 'ChatGPT',
  openai: 'ChatGPT',
  perplexity: 'Perplexity',
  claude: 'Claude',
  anthropic: 'Claude',
  gemini: 'Gemini',
  bard: 'Gemini',
  copilot: 'Microsoft Copilot',
  bingchat: 'Microsoft Copilot',
  grok: 'Grok',
  deepseek: 'DeepSeek',
  metaai: 'Meta AI',
  'you.com': 'You.com',
};

const fromHost = (value: string | null): string | null => {
  if (!value) return null;
  let hostname: string;
  try {
    hostname = new URL(value).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return null;
  }
  const full = `${hostname}${(() => {
    try {
      return new URL(value).pathname.toLowerCase();
    } catch {
      return '';
    }
  })()}`;
  const match = HOST_MATCHERS.find((m) => m.name && m.test(full));
  return match ? match.name : null;
};

const fromUtm = (params: URLSearchParams): string | null => {
  const raw = (params.get('utm_source') || params.get('ref') || '').toLowerCase().replace(/[^a-z0-9.]/g, '');
  if (!raw) return null;
  const key = Object.keys(UTM_MATCHERS).find((k) => raw.includes(k.replace(/[^a-z0-9.]/g, '')));
  return key ? UTM_MATCHERS[key] : null;
};

/** Returns the AI assistant that sent this visitor, or null. Sticky for the session. */
export const getAiReferralSource = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored || null;
  } catch {
    /* storage unavailable */
  }

  const params = new URLSearchParams(window.location.search);
  const detected = fromUtm(params) || fromHost(document.referrer || null);

  try {
    if (detected) sessionStorage.setItem(STORAGE_KEY, detected);
  } catch {
    /* storage unavailable */
  }

  return detected;
};

const GA_FLAG = 'byteline_ai_referral_reported';

/** Sends one ai_referral event to Google Analytics per session. */
export const reportAiReferralToAnalytics = (pagePath: string): string | null => {
  const source = getAiReferralSource();
  if (!source) return null;

  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return source;

  // User scoped property so later pages in the visit stay attributed.
  gtag('set', 'user_properties', { ai_referral_source: source });

  let alreadyReported = false;
  try {
    alreadyReported = sessionStorage.getItem(GA_FLAG) === '1';
  } catch {
    /* storage unavailable */
  }

  if (!alreadyReported) {
    gtag('event', 'ai_referral', {
      ai_source: source,
      landing_page: pagePath,
      page_location: window.location.href,
    });
    try {
      sessionStorage.setItem(GA_FLAG, '1');
    } catch {
      /* storage unavailable */
    }
  }

  return source;
};
